import { PERMISSIONS } from '../constants/permissions';
import type { ReportAccess, ReportModuleCapabilities } from '../types/report';
import { hasAnyPermission, hasPermission } from '../utils/permissions';

/**
 * Report capabilities from mounted backend routes
 * (arkhrmsapi report.routes.js).
 *
 * Mounted and functional:
 * - GET  /reports
 * - POST /reports/:reportCode/run  (format JSON | CSV | XLSX)
 *
 * Intentionally not mounted (404):
 * - GET /reports/executions
 * - GET /reports/executions/:executionId
 * - GET /reports/executions/:executionId/download
 *
 * Permissions (installed DB codes):
 * - REPORT_VIEW   → JSON preview
 * - REPORT_EXPORT → CSV / XLSX export
 *
 * Catalogue access requires either permission (route gate).
 */
export const REPORT_ROUTE_CAPABILITIES = {
  catalogue: true,
  preview: true,
  exportCsv: true,
  exportXlsx: true,
  executionHistory: false,
  executionDetails: false,
  previousExecutionDownload: false,
} as const;

export const REPORT_ACCESS_PERMISSIONS = [
  PERMISSIONS.REPORT_VIEW,
  PERMISSIONS.REPORT_EXPORT,
] as const;

/**
 * Codes that link Reports menu entries to role report access rows
 * (rpt_role_report_access.report_code). Which codes a role may use comes
 * from the backend; these only name each menu entry.
 */
export const REPORT_ACCESS_CODES = {
  DAILY_ATTENDANCE: 'ATTENDANCE_REGISTER',
  WEEKLY_ATTENDANCE: 'WEEKLY_ATTENDANCE',
  /**
   * Org-wide employee weekly grid. Not a catalogue report code: HR Admin opens
   * it with EMPLOYEE_VIEW. Employees granted only WEEKLY_ATTENDANCE stay on
   * the existing per-employee weekly report.
   */
  EMPLOYEE_OVERALL_WEEKLY: 'EMPLOYEE_OVERALL_WEEKLY',
  MONTHLY_ATTENDANCE: 'MONTHLY_ATTENDANCE_SUMMARY',
  LATE_ARRIVAL: 'ATTENDANCE_LATE_ARRIVAL',
  SHORT_HOURS: 'ATTENDANCE_SHORT_HOURS',
} as const;

/** True when a role-restricted user has been granted this report code. */
export function isReportGranted(
  access: ReportAccess | null,
  reportCode: string,
): boolean {
  return Boolean(access?.restricted && access.reportCodes.includes(reportCode));
}

export function getReportModuleCapabilities(
  permissions: readonly string[],
): ReportModuleCapabilities {
  const canAccess = hasAnyPermission(permissions, REPORT_ACCESS_PERMISSIONS);
  const canView = hasPermission(permissions, PERMISSIONS.REPORT_VIEW);
  const canExport = hasPermission(permissions, PERMISSIONS.REPORT_EXPORT);

  return {
    canViewCatalogue: canAccess && REPORT_ROUTE_CAPABILITIES.catalogue,
    canPreview: canView && REPORT_ROUTE_CAPABILITIES.preview,
    canExportCsv: canExport && REPORT_ROUTE_CAPABILITIES.exportCsv,
    canExportXlsx: canExport && REPORT_ROUTE_CAPABILITIES.exportXlsx,
    canViewExecutionHistory:
      canAccess && REPORT_ROUTE_CAPABILITIES.executionHistory,
    canViewExecutionDetails:
      canAccess && REPORT_ROUTE_CAPABILITIES.executionDetails,
    canDownloadPreviousExecution:
      canAccess && REPORT_ROUTE_CAPABILITIES.previousExecutionDownload,
  };
}
